<template>
    <div
        class="group my-6 rounded-lg border-iron-200 bg-iron-25 bg-white shadow-xs w-full border transition-all duration-200"
    >
        <div
            class="border-iron-199 space-x-3 rounded-t-lg bg-iron-25 px-6 pb-6 sm:flex items-center justify-between border-b pt-[18px]"
        >
            <div class="flex items-center">
                <div class="flex-shrink-0"></div>
                <div class="min-w-0 gap-3 flex">
                    <p class="font-medium text-iron-700">
                        {{ team.name }}
                    </p>
                </div>
            </div>

            <div
                v-if="user.id === team.owner.id"
                class="mt-3 sm:mt-0 flex flex-shrink-0 justify-start self-center"
            >
                <div class="gap-3 rounded-md inline-flex">
                    <InviteToTeam :team="team">
                        <Button variant="outline" size="sm">
                            <UserPlus
                                :storkeWidth="1.75"
                                class="mr-2 text-iron-500"
                                :size="20"
                            ></UserPlus>
                            {{ $t("teams.invite") }}</Button
                        >
                    </InviteToTeam>

                    <Link
                        :href="
                            route('app.teams.reports.index', { team: team.id })
                        "
                    >
                        <Button variant="outline" size="sm" as="div"
                            ><ChartColumnStacked
                                :storkeWidth="1.75"
                                :size="20"
                                class="mr-2 text-iron-500"
                            />

                            {{ $t("teams.reports") }}
                        </Button>
                    </Link>
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <Button
                                size="icon"
                                class="h-[36px] w-[36px]"
                                variant="outline"
                            >
                                <EllipsisVertical
                                    :storkeWidth="1.75"
                                    :size="20"
                                    class="text-iron-500"
                                /> </Button
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent :align="'end'">
                            <DropdownMenuItem>
                                <Link
                                    class="gap-2 flex w-full"
                                    :href="route('app.billing')"
                                >
                                    <CreditCard
                                        :storkeWidth="1.75"
                                        class="h-5 w-5 text-iron-500"
                                    ></CreditCard>

                                    {{ $t("teams.billing") }}
                                </Link></DropdownMenuItem
                            >
                            <DropdownMenuSeparator />
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <DropdownMenuItem :disabled="true">
                                            <button
                                                :disabled="true"
                                                class="gap-2 flex w-full items-center text-[#F04438]"
                                                @click="deleteTeam"
                                            >
                                                <Trash
                                                    :storkeWidth="1.75"
                                                    class="h-5 w-5 text-[#F04438]"
                                                ></Trash>

                                                {{ $t("teams.delete") }}
                                            </button>
                                        </DropdownMenuItem>
                                    </TooltipTrigger>
                                    <TooltipContent class="max-w-xs">
                                        <p>
                                            {{
                                                $t(
                                                    "teams.delete_team_support_note",
                                                )
                                            }}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>

        <div
            v-if="isCurrentUserOwner"
            class="border-iron-199 gap-4 px-6 py-4 flex items-center border-b"
        >
            <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-iron-700">
                    {{ $t("teams.logo.title") }}
                </p>
                <p class="text-xs text-iron-500">
                    {{ $t("teams.logo.description") }}
                </p>
            </div>

            <div class="gap-2 flex flex-shrink-0 items-center">
                <img
                    v-if="logoUrl"
                    :src="logoUrl"
                    alt="Team logo"
                    class="h-8 max-w-[120px] object-contain"
                />

                <Button
                    variant="outline"
                    size="sm"
                    :disabled="logoUploading"
                    @click.stop="logoInput?.click()"
                >
                    <Upload :size="16" class="mr-1.5 text-iron-500" />
                    {{
                        logoUrl
                            ? $t("teams.logo.change")
                            : $t("teams.logo.upload")
                    }}
                </Button>

                <Button
                    v-if="logoUrl"
                    variant="ghost"
                    size="sm"
                    @click="deleteLogo"
                >
                    <Trash :size="16" class="text-iron-400" />
                </Button>
            </div>

            <input
                ref="logoInput"
                class="hidden"
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                @input="onLogoFileSelected"
            />
        </div>

        <div>
            <ul role="list" class="divide-iron-200 divide-y">
                <li
                    v-for="teamUser in teamUsers"
                    :key="teamUser.id"
                    class="gap-x-6 px-6 py-3 flex items-center justify-between"
                >
                    <div class="flex w-full justify-between">
                        <div class="min-w-0 gap-x-3 flex items-center">
                            <ImageZaptimeShape
                                v-if="teamUser.profile_photo_path"
                                :src="
                                    'https://zaptime.s3.eu-central-1.amazonaws.com/' +
                                    teamUser.profile_photo_path
                                "
                            />
                            <div
                                v-else
                                class="leading-12 h-10 w-10 bg-orange-200 text-xl font-semibold text-white flex items-center justify-center rounded-full text-center"
                            >
                                <span>
                                    {{ getTwoInitials(teamUser.name) }}
                                </span>
                            </div>
                            <div class="h-10 min-w-0 flex-auto">
                                <p
                                    class="gap-x-3 text-sm font-medium leading-5 text-iron-900 inline-flex"
                                >
                                    {{ teamUser.name }}

                                    <span
                                        v-if="teamUser.id !== team.owner.id"
                                        :class="
                                            roleBadge({
                                                role:
                                                    teamUser.membership.role ===
                                                    'viewer'
                                                        ? 'editor'
                                                        : teamUser.membership
                                                              .role,
                                            })
                                        "
                                        >{{
                                            capitalize(
                                                $t(
                                                    "teams.roles." +
                                                        (teamUser.membership
                                                            .role === "viewer"
                                                            ? "editor"
                                                            : teamUser
                                                                  .membership
                                                                  .role) +
                                                        ".title",
                                                ),
                                            )
                                        }}</span
                                    >

                                    <span
                                        v-else
                                        :class="
                                            roleBadge({
                                                role: 'owner',
                                            })
                                        "
                                        >{{
                                            capitalize(
                                                $t("teams.roles.owner.title"),
                                            )
                                        }}</span
                                    >
                                </p>
                                <p
                                    class="text-xs text-iron-500 truncate leading-[18px] tracking-[-1%]"
                                >
                                    {{ teamUser.email }}
                                </p>
                            </div>
                        </div>

                        <div class="gap-2 flex">
                            <UpdateRole
                                v-if="
                                    teamUser.id !== user.id &&
                                    user.id === team.owner.id
                                "
                                :team="team"
                                :team-user="teamUser"
                            >
                                <Button variant="ghost" size="sm">
                                    {{ $t("teams.update_role") }}
                                </Button>
                            </UpdateRole>

                            <Button
                                v-if="
                                    teamUser.id !== user.id &&
                                    user.id === team.owner.id
                                "
                                variant="ghost"
                                size="sm"
                                @click="removeMember(teamUser.id)"
                            >
                                {{ $t("teams.remove_member") }}
                            </Button>
                        </div>

                        <Button
                            v-if="
                                user.id === teamUser.id && !isCurrentUserOwner
                            "
                            variant="ghost"
                            size="sm"
                            @click="leaveTeam()"
                        >
                            {{ $t("teams.leave") }}
                        </Button>
                    </div>
                </li>

                <li
                    v-for="teamInvitation in team.team_invitations"
                    :key="teamInvitation.id"
                    class="gap-x-6 px-6 py-3 flex items-center justify-between"
                >
                    <div class="flex w-full justify-between">
                        <div class="gap-3 flex">
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <mask
                                    id="mask0_2091_5162"
                                    style="mask-type: alpha"
                                    maskUnits="userSpaceOnUse"
                                    x="0"
                                    y="0"
                                    width="40"
                                    height="40"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M30.1618 0.860585C27.9053 0.344234 24.1962 0 20.0069 0C15.8176 0 12.105 0.344234 9.85198 0.860585C7.6441 1.3264 5.61904 2.42344 4.02286 4.0184C2.42668 5.61336 1.32809 7.63758 0.860585 9.84509C0.344234 12.1015 0 15.8107 0 20C0 24.1893 0.344234 27.9019 0.860585 30.1549C1.32759 32.3615 2.42516 34.3851 4.02003 35.98C5.6149 37.5748 7.63848 38.6724 9.84509 39.1394C12.1015 39.6558 15.8107 40 20 40C24.1893 40 27.9019 39.6558 30.1549 39.1394C32.3615 38.6724 34.3851 37.5748 35.98 35.98C37.5748 34.3851 38.6724 32.3615 39.1394 30.1549C39.6558 27.8985 40 24.1893 40 20C40 15.8107 39.6558 12.0981 39.1394 9.84509C38.6729 7.63939 37.5764 5.61644 35.9828 4.02166C34.3892 2.42687 32.3671 1.32878 30.1618 0.860585Z"
                                        fill="url(#paint0_linear_2091_5162)"
                                    />
                                </mask>
                                <g mask="url(#mask0_2091_5162)">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M30.1618 0.860585C27.9053 0.344234 24.1962 0 20.0069 0C15.8176 0 12.105 0.344234 9.85198 0.860585C7.6441 1.3264 5.61904 2.42344 4.02286 4.0184C2.42668 5.61336 1.32809 7.63758 0.860585 9.84509C0.344234 12.1015 0 15.8107 0 20C0 24.1893 0.344234 27.9019 0.860585 30.1549C1.32759 32.3615 2.42516 34.3851 4.02003 35.98C5.6149 37.5748 7.63848 38.6724 9.84509 39.1394C12.1015 39.6558 15.8107 40 20 40C24.1893 40 27.9019 39.6558 30.1549 39.1394C32.3615 38.6724 34.3851 37.5748 35.98 35.98C37.5748 34.3851 38.6724 32.3615 39.1394 30.1549C39.6558 27.8985 40 24.1893 40 20C40 15.8107 39.6558 12.0981 39.1394 9.84509C38.6729 7.63939 37.5764 5.61644 35.9828 4.02166C34.3892 2.42687 32.3671 1.32878 30.1618 0.860585Z"
                                        fill="#F4EBFF"
                                    />
                                </g>
                                <path
                                    d="M17.5 18.3332H17.5083M22.5 18.3332H22.5083M20 11.6665C18.2319 11.6665 16.5362 12.3689 15.2859 13.6191C14.0357 14.8694 13.3333 16.5651 13.3333 18.3332V28.3332L15.8333 25.8332L17.9166 27.9165L20 25.8332L22.0833 27.9165L24.1666 25.8332L26.6666 28.3332V18.3332C26.6666 16.5651 25.9643 14.8694 24.714 13.6191C23.4638 12.3689 21.7681 11.6665 20 11.6665Z"
                                    stroke="#7F56D9"
                                    stroke-width="1.75"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient
                                        id="paint0_linear_2091_5162"
                                        x1="4.34386"
                                        y1="4.00092"
                                        x2="34.8652"
                                        y2="36.9228"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FF8D47" />
                                        <stop
                                            offset="0.147634"
                                            stop-color="#FF7447"
                                        />
                                        <stop
                                            offset="0.454086"
                                            stop-color="#FF4247"
                                        />
                                        <stop
                                            offset="0.636926"
                                            stop-color="#E92A5B"
                                        />
                                        <stop
                                            offset="0.740367"
                                            stop-color="#DD1C67"
                                        />
                                        <stop
                                            offset="0.842991"
                                            stop-color="#D31071"
                                        />
                                        <stop offset="1" stop-color="#CF0283" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div class="min-w-0 gap-x-3 flex items-center">
                                <p
                                    class="text-xs leading-5 text-iron-500 truncate"
                                >
                                    {{ teamInvitation.email }}
                                </p>

                                <span :class="roleBadge({ role: 'pending' })">{{
                                    capitalize($t("teams.pending"))
                                }}</span>
                            </div>
                        </div>

                        <Button
                            variant="ghost"
                            size="sm"
                            @click="deleteInvitation(teamInvitation.id)"
                        >
                            {{ $t("teams.cancel_invitation") }}
                        </Button>
                    </div>
                </li>

                <li
                    v-if="
                        teamUsers.length <= 1 &&
                        team.team_invitations.length === 0
                    "
                    class="rounded-b-lg bg-iron-25 py-6 flex items-center justify-center"
                >
                    <div class="flex flex-col items-center">
                        <InviteToTeam :team="team">
                            <Button size="icon" variant="outline">
                                <UserPlus
                                    :storkeWidth="1.75"
                                    class="text-iron-500"
                                    :size="20"
                                ></UserPlus>
                            </Button>
                        </InviteToTeam>
                        <p class="mt-3 text-sm text-iron-500">
                            <InviteToTeam :team="team">
                                <Button size="sm" class="px-1" variant="ghost">
                                    {{ $t("teams.invite_more_1") }}
                                </Button>
                            </InviteToTeam>

                            {{ $t("teams.invite_more_2") }}
                        </p>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { router, usePage, Link } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import route from "ziggy-js";
import InviteToTeam from "@/Pages/Zaptime/App/Teams/components/InviteToTeam.vue";
import UpdateRole from "@/Pages/Zaptime/App/Teams/components/UpdateRole.vue";
import {
    CreditCard,
    UserPlus,
    EllipsisVertical,
    Trash,
    ChartColumnStacked,
    Upload,
} from "lucide-vue-next";
import { useToast } from "@/components/ui/toast/use-toast";
import type { Team } from "../types/Team";
import { cva } from "class-variance-authority";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalize } from "../../../../../utils/capitalize";
import ImageZaptimeShape from "@/components/ImageZaptimeShape.vue";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const roleBadge = cva(
    [
        "rounded-full border-[1px] px-[9px] flex items-center leading-[18px] text-xs font-medium tracking-[-1%]",
    ],
    {
        variants: {
            role: {
                owner: ["bg-[#FFF4ED] border-[#FFD6AE] text-[#E62E05]"],
                admin: [
                    "bg-z-primary-50 border-z-primary-200 text-z-primary-600",
                ],
                viewer: ["bg-iron-50 text-iron-600 border-iron-300"],
                editor: ["bg-[#EDFCF2] border-[#AAF0C4] text-[#099250]"],
                pending: ["border-[#B2CCFF] bg-[#EFF4FF] text-[#155EEF]"],
            },
        },
    },
);

interface Props {
    team: Team;
}
const { show } = useConfirm();
const { t } = useI18n();

const props = defineProps<Props>();

const page = usePage();
const loading = ref(false);
const user = computed((): any => page.props.user);
const { toast } = useToast();
const logoUrl = computed(() => props.team.logo_url);
const logoUploading = ref(false);
const logoInput = ref<HTMLInputElement>();

function onLogoFileSelected() {
    if (!logoInput.value?.files?.length) return;

    const file = logoInput.value.files[0];

    if (file.size > 1024 * 1024) {
        toast({
            title: t("profile.image_validation.title"),
            description: t("profile.image_validation.description"),
            variant: "destructive",
        });
        logoInput.value.value = "";
        return;
    }

    router.post(
        route("app.teams.logo.store", { team: props.team.id }),
        { image: file },
        {
            forceFormData: true,
            preserveScroll: true,
            onStart: () => {
                logoUploading.value = true;
            },
            onFinish: () => {
                logoUploading.value = false;
                if (logoInput.value) {
                    logoInput.value.value = "";
                }
            },
        },
    );
}

async function deleteLogo() {
    const result = await show({
        header: t("teams.logo.delete_confirmation"),
        message: t("teams.logo.delete_confirmation_description"),
        confirm: t("teams.logo.remove"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(route("app.teams.logo.destroy", { team: props.team.id }), {
        preserveScroll: true,
    });
}

const isCurrentUserOwner = computed(() => {
    return props.team.owner.id === user.value.id;
});

const deleteTeam = async function () {
    const result = await show({
        header: t("teams.delete_confirmation.are_you_sure"),
        message: t("teams.delete_confirmation.description"),
        confirm: t("teams.delete"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("app.teams.delete", {
            team: props.team.id,
        }),
    );
};

function getTwoInitials(name: string) {
    const words = name.trim().split(" ");
    // Ensure there's at least one word to avoid errors
    if (words.length === 1) {
        return words[0][0] + (words[0][1] ? words[0][1] : "");
    } else {
        return words[0][0] + words[words.length - 1][0];
    }
}

async function removeMember(userId: number) {
    const result = await show({
        header: t("teams.remove_member_confirmation.are_you_sure"),
        message: t("teams.remove_member_confirmation.description"),
        confirm: t("teams.remove_member"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-members.destroy", {
            user: userId,
            team: props.team.id,
        }),
    );
}

// takes in team.users and team.owner, returns the logged in user on a 0 index, owner on 1 and others after
const teamUsers = computed(() => {
    //check if logged in user is a team  owner

    if (user.value.id === props.team.owner.id) {
        return [props.team.owner, ...props.team.users];
    }

    const loggedInUserFromTeamUsers = props.team.users.find(
        (teamUser) => teamUser.id === user.value.id,
    );

    if (!loggedInUserFromTeamUsers) {
        return [props.team.owner, ...props.team.users];
    }

    const restOfTeamMemebresWithoutLoggedInUser = props.team.users.filter(
        (teamUser) => {
            return teamUser.id !== user.value.id;
        },
    );

    return [
        loggedInUserFromTeamUsers,
        props.team.owner,
        ...restOfTeamMemebresWithoutLoggedInUser,
    ];
});

async function leaveTeam() {
    const result = await show({
        header: t("teams.leave_confirmation.are_you_sure"),
        message: t("teams.leave_confirmation.description"),
        confirm: t("teams.leave"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-members.destroy", {
            user: user.value.id,
            team: props.team.id,
        }),
    );
}

async function deleteInvitation(invitationId: number) {
    const result = await show({
        header: t("teams.leave_confirmation.are_you_sure"),
        message: t("teams.leave_confirmation.description"),
        confirm: t("teams.cancel_invitation"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-invitations.destroy", {
            invitation: invitationId,
        }),
    );
}
</script>
